// src/hooks/useRequestPolling.ts
// Polling periódico de solicitudes del negocio: detecta cuando NIIZ aprobó/rechazó
// una solicitud y notifica al usuario, restaurando SOLO lo de esa solicitud.

import { useEffect } from 'react';
import { toast } from 'sonner';
import { pollMyRequests, markRequestSeen } from '@/lib/requests';
import { db } from '@/db/db';
import { SyncThrottle, isOnlineNow, logSyncIssue } from '@/lib/syncController';

const POLL_INTERVAL_MS = 15000; // 15 segundos mientras todo va bien
// El polling de solicitudes también respeta online + backoff (10s→30s→1min→5min).
const pollThrottle = new SyncThrottle({ minIntervalMs: POLL_INTERVAL_MS });

export const useRequestPolling = (businessId?: string) => {
  useEffect(() => {
    if (!businessId) return;

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const schedule = () => {
      if (cancelled) return;
      // Sin internet no se agenda ninguna petición a la nube.
      if (!isOnlineNow()) return;
      const wait = Math.max(POLL_INTERVAL_MS, pollThrottle.nextAllowedIn());
      timer = setTimeout(() => void tick().then(schedule), wait);
    };

    const tick = async () => {
      if (cancelled) return;
      if (!isOnlineNow()) return;
      if (!pollThrottle.canRun({ manual: true })) return;

      pollThrottle.markRun();
      try {
        const changed = await pollMyRequests(businessId);
        pollThrottle.onSuccess();
        for (const req of changed) {
          const status = req.status;
          if (status === 'APPROVED') {
            toast.success(`✅ Tu solicitud "${req.subject}" fue APROBADA.`);
            // Restaurar SOLO lo de esta solicitud (no toca trabajadores/clientes/productos).
            if (req.request_type === 'RESTORE') {
              // TODO: aplicar restauración específica según el request_type.
              // Por ahora solo notifica; la restauración puntual se implementa por tipo.
            }
            // Guardar local para reflejar el estado
            await db.support_requests.put(req as any);
            await markRequestSeen(req.id);
          } else if (status === 'REJECTED') {
            toast.error(`❌ Tu solicitud "${req.subject}" fue RECHAZADA.`);
            await db.support_requests.put(req as any);
            await markRequestSeen(req.id);
          }
        }
      } catch (e) {
        // Falla silenciosa + backoff (sin spam de consola ni reintentos cada 15s).
        const { delay, kind } = pollThrottle.onFailure(e);
        logSyncIssue(`Polling de solicitudes en espera (${kind}, ${Math.round(delay / 1000)}s).`);
      } finally {
        pollThrottle.finishRun();
      }
    };

    tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [businessId]);
};
